import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionFilterSchema from "permissions/_api/queries/filtering/permission-filter-schema";
import PermissionOrderParameters, {
  PermissionOrderField,
} from "permissions/_api/queries/ordering/permission-order-params";
import PermissionOrderSchema from "permissions/_api/queries/ordering/permission-order-params-schema";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import { z } from "zod";

export type PermissionTemplateQueryJSON = z.infer<typeof PermissionTemplateQuerySchema.PermissionTemplateQueryJSONZ>;

export type PermissionTemplateQueryParams = z.infer<typeof PermissionTemplateQuerySchema.PermissionTemplateQueryZ>;

export default class PermissionTemplateQuerySchema {
  public static readonly PermissionTemplateQueryJSONZ = z.object({
    filter: PermissionFilterSchema.PermissionFilterJSONZ.nullish(),
    order: PermissionOrderSchema.PermissionOrderZ.nullish(),
    pagination: PaginationSchema.PaginationZ.nullish(),
  });

  public static readonly PermissionTemplateQueryZ = z.object({
    filterParameters: PermissionFilterSchema.PermissionFilterZ.optional(),
    orderParameters: PermissionOrderSchema.PermissionOrderZ.optional(),
    paginationParameters: PaginationSchema.PaginationZ.optional(),
  });

  public static validateJSON(values: unknown): PermissionTemplateQueryJSON {
    return parseOrThrow(PermissionTemplateQuerySchema.PermissionTemplateQueryJSONZ, values, {
      logError: true,
      schemaName: "PermissionTemplateQueryJSON",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is PermissionTemplateQueryJSON {
    try {
      PermissionTemplateQuerySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: PermissionTemplateQueryJSON): PermissionTemplateQuery {
    const parsed = PermissionTemplateQuerySchema.validateJSON(json);

    const filterParameters = new PermissionFilterParameters({
      hasParent: parsed.filter?.hasParent ?? undefined,
      resource: parsed.filter?.resource,
      parentResource: parsed.filter?.parentResource,
      parentId: Guid.fromJSON(parsed.filter?.parentId) ?? undefined,
      legalEntityId: Guid.fromJSON(parsed.filter?.legalEntityId) ?? undefined,
      granteeUserIds: Guid.fromJSONArray(parsed.filter?.granteeUserIds) ?? undefined,
      grantorUserId: Guid.fromJSON(parsed.filter?.grantorUserId) ?? undefined,
      minCreatedDate: AHDate.fromJSON(parsed.filter?.minCreatedDate) ?? undefined,
      maxCreatedDate: AHDate.fromJSON(parsed.filter?.maxCreatedDate) ?? undefined,
      showDisabled: parsed.filter?.showDisabled ?? undefined,
      minDisabledDate: AHDate.fromJSON(parsed.filter?.minDisabledDate) ?? undefined,
      maxDisabledDate: AHDate.fromJSON(parsed.filter?.maxDisabledDate) ?? undefined,
      categories: parsed.filter?.categories ?? undefined,
      accessType: parsed.filter?.accessType ?? undefined,
      name: parsed.filter?.name ?? undefined,
    });

    const orderParameters = parsed.order
      ? new PermissionOrderParameters(parsed.order)
      : new PermissionOrderParameters({
          orderBy: PermissionOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        });

    const paginationParameters = PaginationParameters.create({
      pageIndex: parsed.pagination?.pageIndex ?? 0,
      pageSize: parsed.pagination?.pageSize ?? 25,
    });

    return new PermissionTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
    });
  }

  public static serialize(query: PermissionTemplateQuery): PermissionTemplateQueryJSON {
    const { filterParameters, orderParameters, paginationParameters } = query;

    const json: PermissionTemplateQueryJSON = {
      filter: {
        hasParent: filterParameters?.hasParent,
        resource: filterParameters?.resource,
        parentResource: filterParameters?.parentResource,
        parentId: filterParameters?.parentId?.toJSON(),
        legalEntityId: filterParameters?.legalEntityId?.toJSON(),
        granteeUserIds: filterParameters?.granteeUserIds?.map((id: Guid) => id.toJSON()),
        grantorUserId: filterParameters?.grantorUserId?.toJSON(),
        minCreatedDate: filterParameters?.minCreatedDate?.toJSON(),
        maxCreatedDate: filterParameters?.maxCreatedDate?.toJSON(),
        showDisabled: filterParameters?.showDisabled,
        minDisabledDate: filterParameters?.minDisabledDate?.toJSON(),
        maxDisabledDate: filterParameters?.maxDisabledDate?.toJSON(),
        categories: filterParameters?.categories,
        accessType: filterParameters?.accessType,
        name: filterParameters?.name,
      },
      order: {
        orderBy: orderParameters?.orderBy,
        orderDir: orderParameters?.orderDir,
      },
      pagination: {
        pageIndex: paginationParameters?.pageIndex ?? 0,
        pageSize: paginationParameters?.pageSize ?? 25,
      },
    };

    return parseOrThrow(PermissionTemplateQuerySchema.PermissionTemplateQueryJSONZ, json, {
      logError: true,
      schemaName: "PermissionTemplateQueryJSON",
      operation: "serialize",
      payloadSample: json,
    });
  }
}
